import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { ArrowRight, CheckCircle, Star, Building, Zap, Users, Shield } from 'lucide-react';
import { Card, CardContent } from '../ui/card';

interface RelatedTool {
  name: string;
  description: string;
  href: string;
}

interface SolutionDetailTemplateProps {
  title: string;
  description: string;
  category: string;
  categoryColor: string;
  keyFeatures: string[];
  benefits: string[];
  useCases: string[];
  relatedTools?: RelatedTool[];
  industries?: string[];
}

export const SolutionDetailTemplate: React.FC<SolutionDetailTemplateProps> = ({
  title,
  description,
  category,
  categoryColor,
  keyFeatures,
  benefits,
  useCases,
  relatedTools = [],
  industries = []
}) => {
  return (
    <div className="min-h-screen pt-24">
      {/* Hero Section */}
      <section className="pt-5 pb-2 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-secondary/10" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <div className="flex items-center gap-3 mb-6">
              <span 
                className="px-4 py-2 rounded-full text-sm font-semibold"
                style={{ 
                  backgroundColor: `${categoryColor}20`,
                  color: categoryColor 
                }}
              >
                {category}
              </span>
            </div>
            
            <motion.h1
              className="text-4xl md:text-6xl mb-6 neon-text"
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              transition={{ duration: 1, delay: 0.2 }}
            >
              {title}
            </motion.h1>
            
            <motion.p
              className="text-xl md:text-2xl text-muted-foreground mb-8 leading-relaxed"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.5 }}
            >
              {description}
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row gap-4"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
            >
              <Link 
                to="/contact"
                className="bg-primary hover:bg-primary/80 text-primary-foreground px-8 py-4 rounded-lg transition-all duration-300 neon-glow flex items-center gap-2 w-fit"
              >
                <span>Request Demo</span>
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link 
                to="/solutions"
                className="border border-primary/50 text-primary hover:bg-primary/10 px-8 py-4 rounded-lg transition-all duration-300 flex items-center gap-2 w-fit"
              >
                Back to Solutions
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Key Features Section */}
      <section className="py-5 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="mb-4">Key Features</h2>
            <p className="text-xl text-muted-foreground text-center max-w-3xl mx-auto">
              Discover the powerful capabilities that make {title} the perfect solution for your needs.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {keyFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="glass-strong h-full hover:scale-105 transition-all duration-300 group">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-accent mt-1 flex-shrink-0" />
                      <div>
                        <p className="group-hover:text-primary transition-colors">
                          {feature}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 relative">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-secondary/5" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="mb-6">Benefits & Value</h2>
              <p className="text-xl text-muted-foreground mb-8">
                Experience the transformative impact of {title} on your business operations.
              </p>
              
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <motion.div
                    key={index}
                    className="flex items-start gap-3"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <Star className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                    <span className="text-muted-foreground">{benefit}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="grid grid-cols-2 gap-6">
                <Card className="glass-strong hover:scale-105 transition-all duration-300">
                  <CardContent className="p-6 text-center">
                    <Users className="w-12 h-12 text-primary mx-auto mb-4" />
                    <h3 className="text-2xl font-bold mb-2">500+</h3>
                    <p className="text-muted-foreground text-sm text-center">Active Users</p>
                  </CardContent>
                </Card>
                
                <Card className="glass-strong hover:scale-105 transition-all duration-300">
                  <CardContent className="p-6 text-center">
                    <Zap className="w-12 h-12 text-accent mx-auto mb-4" />
                    <h3 className="text-2xl font-bold mb-2">99.9%</h3>
                    <p className="text-muted-foreground text-sm text-center">Uptime</p>
                  </CardContent>
                </Card>
                
                <Card className="glass-strong hover:scale-105 transition-all duration-300">
                  <CardContent className="p-6 text-center">
                    <Shield className="w-12 h-12 text-secondary mx-auto mb-4" />
                    <h3 className="text-2xl font-bold mb-2">100%</h3>
                    <p className="text-muted-foreground text-sm text-center">Secure</p>
                  </CardContent>
                </Card>
                
                <Card className="glass-strong hover:scale-105 transition-all duration-300">
                  <CardContent className="p-6 text-center">
                    <CheckCircle className="w-12 h-12 text-primary mx-auto mb-4" />
                    <h3 className="text-2xl font-bold mb-2">24/7</h3>
                    <p className="text-muted-foreground text-sm text-center">Support</p>
                  </CardContent>
                </Card>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-24 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="mb-4">Use Cases</h2>
            <p className="text-xl text-muted-foreground text-center max-w-3xl mx-auto">
              See how {title} can be applied across different scenarios and industries.
            </p>
          </motion.div>

          <div className="flex flex-wrap justify-center gap-8">
            {useCases.map((useCase, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="w-full md:w-[calc(50%-1rem)] lg:w-[calc(33.333%-1.34rem)]"
              >
                <Card className="glass-strong h-full hover:scale-105 transition-all duration-300 group">
                  <CardContent className="p-6">
                    <div 
                      className="w-12 h-12 rounded-lg mb-4 flex items-center justify-center"
                      style={{ backgroundColor: `${categoryColor}20` }}
                    >
                      <div 
                        className="w-6 h-6 rounded-full"
                        style={{ backgroundColor: categoryColor }}
                      />
                    </div>
                    <p className="group-hover:text-primary transition-colors">
                      {useCase}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Tools Section */}
      {relatedTools.length > 0 && (
        <section className="py-24 relative">
          <div className="absolute inset-0 bg-gradient-to-r from-secondary/5 to-accent/5" />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="mb-4">Related Tools & Products</h2>
              <p className="text-xl text-muted-foreground text-center max-w-3xl mx-auto">
                Explore other solutions that complement {title} in your digital transformation journey.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {relatedTools.map((tool, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Link to={tool.href}>
                    <Card className="glass-strong h-full hover:scale-105 transition-all duration-300 group">
                      <CardContent className="p-6 text-center">
                        <h3 className="font-semibold mb-2 group-hover:text-primary transition-colors">
                          {tool.name}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {tool.description}
                        </p>
                        <div className="mt-4 text-primary">
                          <ArrowRight className="w-4 h-4 mx-auto group-hover:translate-x-1 transition-transform" />
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Industries Section */}
      {industries.length > 0 && (
        <section className="py-24 relative">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="mb-4">Industries We Serve</h2>
              <p className="text-xl text-muted-foreground text-center max-w-3xl mx-auto">
                {title} is trusted across various industries to deliver exceptional results.
              </p>
            </motion.div>

            <div className="flex flex-wrap justify-center gap-4">
              {industries.map((industry, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <span 
                    className="px-6 py-3 rounded-full text-sm font-medium border border-primary/30 hover:border-primary/60 transition-colors flex items-center gap-2"
                    style={{ 
                      backgroundColor: `${categoryColor}10`,
                      color: categoryColor 
                    }}
                  >
                    <Building className="w-4 h-4" />
                    {industry}
                  </span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-24 relative">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-accent/5 to-secondary/10" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="mb-6">Ready to Get Started?</h2>
            <p className="text-xl text-muted-foreground text-center mb-8">
              Contact our team to learn more about {title} and schedule a personalized demo.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-primary hover:bg-primary/80 text-primary-foreground px-8 py-4 rounded-lg transition-all duration-300 neon-glow text-lg"
              >
                Schedule Demo
              </Link>
              <Link
                to="/case-studies"
                className="border border-accent text-accent hover:bg-accent/10 px-8 py-4 rounded-lg transition-all duration-300 text-lg"
              >
                View Case Studies
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};